'use memo';

import {
  type UserIntegrationAccess,
  type UserIntegrationMetadata,
} from '@urbanx/agx.agencies.client';
import { AgxBadge, AgxButton, AgxSpacer, AgxTypography } from '@urbanx/agx.ui-components2';
import clsx from 'clsx';

interface ConnectionCardProps {
  name: string;
  description: string;
  integration?: UserIntegrationAccess;
  authorizationUrl?: string;
}

const getConnectionDetails = (metadata?: UserIntegrationMetadata) => {
  if (!metadata) return 'Connection successful.';

  const user = metadata.email ?? metadata.name ?? metadata.userId;

  return user ? `Connection successful with ${user}` : 'Connection successful.';
};

const navigateToProvider = (authorizationUrl?: string) => {
  if (authorizationUrl) window.location.assign(authorizationUrl);
};

const ConnectionCard = ({
  name,
  description,
  integration,
  authorizationUrl,
}: ConnectionCardProps) => {
  const id = name.toLowerCase().replaceAll(' ', '-');
  const isConnected = integration?.hasAccess === true;
  const isClickable = !isConnected && !!authorizationUrl;
  const cardClassName = clsx(
    'onboarding-connection-card',
    isConnected && 'onboarding-connection-card--connected'
  );

  const content = (
    <>
      <AgxSpacer
        direction="horizontal"
        contentAlignment="center"
        contentJustification="space-between"
      >
        <AgxTypography id={`onboarding-${id}-name`} type="label" size="large">
          {name}
        </AgxTypography>
        <AgxBadge
          type="colourfill"
          size="md"
          colour={isConnected ? 'lime' : 'lightgrey'}
          text={isConnected ? 'Connected' : 'Connect Now'}
        />
      </AgxSpacer>
      <AgxTypography
        id={`onboarding-${id}-description`}
        type="body_ui"
        size="small"
        color="var(--text-tertiary, #94a3b8)"
      >
        {isConnected ? getConnectionDetails(integration.metadata) : description}
      </AgxTypography>
      {isConnected && (
        <AgxButton
          id={`reconnect-${id}`}
          text="Reconnect"
          variant="linkBrand"
          size="medium"
          disabled={!authorizationUrl}
          onClick={() => navigateToProvider(authorizationUrl)}
        />
      )}
    </>
  );

  return isClickable ? (
    <a
      id={`onboarding-${id}-connection`}
      data-testid={`onboarding-${id}-connection`}
      href={authorizationUrl}
      className={cardClassName}
    >
      <AgxSpacer direction="vertical" gap="spacing-lg" padding="spacing-xl">
        {content}
      </AgxSpacer>
    </a>
  ) : (
    <AgxSpacer
      id={`onboarding-${id}-connection`}
      data-testid={`onboarding-${id}-connection`}
      direction="vertical"
      gap="spacing-lg"
      padding="spacing-xl"
      className={cardClassName}
      aria-disabled={!isConnected || undefined}
    >
      {content}
    </AgxSpacer>
  );
};

export default ConnectionCard;
